import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { 
  Briefcase, CheckCircle, Award, Shield, Upload,
  Phone, Mail, User, Building2, FileText, AlertCircle,
  TreePine, HardHat, ClipboardCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CareersPage() {
  const [scrolled, setScrolled] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    applicant_type: "",
    company_name: "",
    full_name: "",
    email: "",
    phone: "",
    
    // Step 2: Position & Certification
    position: "",
    certification_number: "",
    years_experience: "",
    
    // Step 3: Insurance (Required)
    has_insurance: false,
    insurance_company: "",
    insurance_policy_number: "",
    insurance_expiration: "",
    
    // Step 4: Resume & Message
    resume_text: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const positions = [
    { value: "arborist", label: "ISA Certified Arborist", requiresCert: true },
    { value: "skilled-trimmer", label: "Skilled Tree Trimmer", requiresCert: false },
    { value: "climber", label: "Tree Climber", requiresCert: false },
    { value: "ground-crew", label: "Ground Crew", requiresCert: false },
    { value: "equipment-operator", label: "Equipment Operator", requiresCert: false },
  ];

  const selectedPosition = positions.find(p => p.value === formData.position);
  const requiresCertification = selectedPosition?.requiresCert || false;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const canProceedStep1 = formData.applicant_type && formData.full_name && formData.email && formData.phone &&
    (formData.applicant_type === "individual" || (formData.applicant_type === "company" && formData.company_name));

  const canProceedStep2 = formData.position && formData.years_experience &&
    (!requiresCertification || (requiresCertification && formData.certification_number));

  const canProceedStep3 = formData.has_insurance && formData.insurance_company && 
    formData.insurance_policy_number && formData.insurance_expiration;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/job-application`, formData);
      if (response.data.success) {
        toast.success("Application submitted successfully! We'll be in touch soon.");
        setStep(5); // Success state
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Careers - Join Our Team | Celebrity Tree Service | Scottsdale, Paradise Valley AZ</title>
        <meta name="description" content="Join Arizona's premier tree care team. Now hiring ISA Certified Arborists, skilled trimmers, and climbers. Competitive pay, great benefits. Apply today!" />
        <meta name="keywords" content="tree service jobs, arborist jobs Arizona, tree trimmer jobs Scottsdale, tree climber careers, tree service employment Phoenix" />
        <link rel="canonical" href="https://paradisevalleytreeservice.com/careers" />
      </Helmet>

      <div className="min-h-screen bg-[#F9F9F5]" data-testid="careers-page">
        <Navigation scrolled={scrolled} />

        {/* Hero Section */}
        <section 
          className="relative min-h-[50vh] flex items-center pt-32 pb-16"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(10, 22, 40, 0.95), rgba(10, 22, 40, 0.7)), url('https://images.unsplash.com/photo-1657730390984-13ab529bb175?w=1920&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {/* Accepting Applications Banner */}
            <div className="inline-block mb-8 animate-pulse">
              <div className="bg-gradient-to-r from-[#10B981] to-[#059669] px-8 py-4 shadow-xl shadow-emerald-500/30">
                <p className="text-white font-bold text-2xl md:text-3xl tracking-wider uppercase">
                  Now Accepting Applications
                </p>
              </div>
            </div>
            
            <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Join Arizona's Premier
              <br />
              <span className="text-[#D4A853]">Tree Care Team</span>
            </h1>
            
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
              We're growing and looking for passionate professionals who share our 
              commitment to excellence. Great pay, benefits, and a supportive team environment.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2">
                <CheckCircle className="w-5 h-5 text-[#10B981]" />
                <span className="text-white">Competitive Pay</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2">
                <CheckCircle className="w-5 h-5 text-[#10B981]" />
                <span className="text-white">Benefits Package</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2">
                <CheckCircle className="w-5 h-5 text-[#10B981]" />
                <span className="text-white">Career Growth</span>
              </div>
            </div>
          </div>
        </section>

        {/* Open Positions */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[#0A1628] text-center mb-12">
              Open Positions
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: Award, title: "ISA Certified Arborist", desc: "Lead tree assessments and complex projects", req: "ISA Certification Required" },
                { icon: TreePine, title: "Skilled Tree Trimmer", desc: "Expert pruning and shaping for luxury properties", req: "3+ Years Experience" },
                { icon: HardHat, title: "Tree Climber", desc: "Safe climbing and aerial work", req: "Climbing Experience Required" },
              ].map((job, idx) => (
                <div key={idx} className="bg-[#F8F6F1] p-6 border-l-4 border-[#10B981]">
                  <job.icon className="w-10 h-10 text-[#10B981] mb-4" />
                  <h3 className="font-bold text-[#0A1628] text-xl mb-2">{job.title}</h3>
                  <p className="text-[#64748B] mb-3">{job.desc}</p>
                  <p className="text-[#10B981] text-sm font-semibold">{job.req}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application Form */}
        <section className="py-20 bg-[#FDFCF9]" id="apply">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase mb-4">
                Apply Now
              </p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl font-bold text-[#0A1628] mb-4">
                Job Application
              </h2>
              <p className="text-[#64748B]">
                Complete all steps to submit your application. Business insurance verification is required.
              </p>
            </div>

            {/* Progress Steps */}
            <div className="flex justify-between mb-12">
              {["Contact Info", "Position", "Insurance", "Submit"].map((label, idx) => (
                <div key={idx} className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                    step > idx + 1 ? "bg-[#10B981] text-white" :
                    step === idx + 1 ? "bg-[#0A1628] text-white" :
                    "bg-gray-200 text-gray-500"
                  }`}>
                    {step > idx + 1 ? <CheckCircle className="w-5 h-5" /> : idx + 1}
                  </div>
                  <span className="text-xs mt-2 text-[#64748B] hidden sm:block">{label}</span>
                </div>
              ))}
            </div>

            {/* Form Container */}
            <div className="bg-white p-8 shadow-lg" data-testid="application-form">
              
              {/* Step 1: Contact Information */}
              {step === 1 && (
                <div className="space-y-6">
                  <h3 className="font-bold text-[#0A1628] text-xl mb-6 flex items-center gap-2">
                    <User className="w-5 h-5 text-[#10B981]" />
                    Contact Information
                  </h3>

                  <div>
                    <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                      Are you applying as... *
                    </label>
                    <Select onValueChange={(v) => handleInputChange("applicant_type", v)}>
                      <SelectTrigger className="border-[#10B981]/30">
                        <SelectValue placeholder="Select application type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual">Individual Applicant</SelectItem>
                        <SelectItem value="company">Company / Subcontractor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.applicant_type === "company" && (
                    <div>
                      <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                        Company Name *
                      </label>
                      <Input
                        value={formData.company_name}
                        onChange={(e) => handleInputChange("company_name", e.target.value)}
                        placeholder="Your Company Name"
                        className="border-[#10B981]/30"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                      Full Name *
                    </label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => handleInputChange("full_name", e.target.value)}
                      placeholder="John Smith"
                      className="border-[#10B981]/30"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                        Email *
                      </label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        placeholder="john@example.com"
                        className="border-[#10B981]/30"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                        Phone *
                      </label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                        placeholder="(555) 123-4567"
                        className="border-[#10B981]/30"
                      />
                    </div>
                  </div>

                  <Button
                    onClick={() => setStep(2)}
                    disabled={!canProceedStep1}
                    className="w-full bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-none py-5 uppercase tracking-wider font-bold"
                  >
                    Continue to Position
                  </Button>
                </div>
              )}

              {/* Step 2: Position & Certification */}
              {step === 2 && (
                <div className="space-y-6">
                  <h3 className="font-bold text-[#0A1628] text-xl mb-6 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-[#10B981]" />
                    Position & Qualifications
                  </h3>

                  <div>
                    <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                      Position Applying For *
                    </label>
                    <Select onValueChange={(v) => handleInputChange("position", v)}>
                      <SelectTrigger className="border-[#10B981]/30">
                        <SelectValue placeholder="Select position" />
                      </SelectTrigger>
                      <SelectContent>
                        {positions.map(pos => (
                          <SelectItem key={pos.value} value={pos.value}>{pos.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {requiresCertification && (
                    <div className="bg-[#D4A853]/10 border border-[#D4A853]/30 p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-[#D4A853] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-[#0A1628] mb-2">
                            Certification Required
                          </p>
                          <p className="text-sm text-[#64748B] mb-3">
                            ISA Certified Arborist position requires a valid certification number.
                          </p>
                          <Input
                            value={formData.certification_number}
                            onChange={(e) => handleInputChange("certification_number", e.target.value)}
                            placeholder="ISA Certification Number (e.g., AZ-1234A)"
                            className="border-[#D4A853]/30"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                      Years of Experience *
                    </label>
                    <Select onValueChange={(v) => handleInputChange("years_experience", v)}>
                      <SelectTrigger className="border-[#10B981]/30">
                        <SelectValue placeholder="Select experience level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0-1">Less than 1 year</SelectItem>
                        <SelectItem value="1-3">1-3 years</SelectItem>
                        <SelectItem value="3-5">3-5 years</SelectItem>
                        <SelectItem value="5-10">5-10 years</SelectItem>
                        <SelectItem value="10+">10+ years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-4">
                    <Button
                      onClick={() => setStep(1)}
                      variant="outline"
                      className="flex-1 rounded-none py-5"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={() => setStep(3)}
                      disabled={!canProceedStep2}
                      className="flex-1 bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-none py-5 uppercase tracking-wider font-bold"
                    >
                      Continue to Insurance
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Insurance Verification (REQUIRED) */}
              {step === 3 && (
                <div className="space-y-6">
                  <h3 className="font-bold text-[#0A1628] text-xl mb-2 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-[#10B981]" />
                    Business Insurance Verification
                  </h3>
                  
                  <div className="bg-red-50 border border-red-200 p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-red-800">Required</p>
                        <p className="text-sm text-red-700">
                          All applicants must provide valid business liability insurance information 
                          to proceed. This is mandatory for all positions.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-[#F8F6F1] border border-[#10B981]/20">
                    <input
                      type="checkbox"
                      id="has_insurance"
                      checked={formData.has_insurance}
                      onChange={(e) => handleInputChange("has_insurance", e.target.checked)}
                      className="w-5 h-5 accent-[#10B981]"
                    />
                    <label htmlFor="has_insurance" className="text-[#0A1628] font-semibold">
                      I confirm I have valid business liability insurance
                    </label>
                  </div>

                  {formData.has_insurance && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                          Insurance Company Name *
                        </label>
                        <Input
                          value={formData.insurance_company}
                          onChange={(e) => handleInputChange("insurance_company", e.target.value)}
                          placeholder="e.g., State Farm, Allstate, etc."
                          className="border-[#10B981]/30"
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                            Policy Number *
                          </label>
                          <Input
                            value={formData.insurance_policy_number}
                            onChange={(e) => handleInputChange("insurance_policy_number", e.target.value)}
                            placeholder="Policy Number"
                            className="border-[#10B981]/30"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                            Expiration Date *
                          </label>
                          <Input
                            type="date"
                            value={formData.insurance_expiration}
                            onChange={(e) => handleInputChange("insurance_expiration", e.target.value)}
                            className="border-[#10B981]/30"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex gap-4">
                    <Button
                      onClick={() => setStep(2)}
                      variant="outline"
                      className="flex-1 rounded-none py-5"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={() => setStep(4)}
                      disabled={!canProceedStep3}
                      className="flex-1 bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-none py-5 uppercase tracking-wider font-bold"
                    >
                      Continue to Submit
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 4: Resume & Final Submit */}
              {step === 4 && (
                <div className="space-y-6">
                  <h3 className="font-bold text-[#0A1628] text-xl mb-6 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#10B981]" />
                    Resume & Additional Info
                  </h3>

                  <div>
                    <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                      Resume / Work Experience
                    </label>
                    <Textarea
                      value={formData.resume_text}
                      onChange={(e) => handleInputChange("resume_text", e.target.value)}
                      placeholder="Paste your resume or describe your relevant work experience, skills, and certifications..."
                      className="border-[#10B981]/30 min-h-[150px]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                      Additional Message (Optional)
                    </label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => handleInputChange("message", e.target.value)}
                      placeholder="Anything else you'd like us to know?"
                      className="border-[#10B981]/30"
                    />
                  </div>

                  {/* Application Summary */}
                  <div className="bg-[#F8F6F1] p-6 border border-[#10B981]/20">
                    <h4 className="font-semibold text-[#0A1628] mb-4">Application Summary</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="text-[#64748B]">Name:</span> <span className="font-semibold">{formData.full_name}</span></p>
                      <p><span className="text-[#64748B]">Position:</span> <span className="font-semibold">{positions.find(p => p.value === formData.position)?.label}</span></p>
                      <p><span className="text-[#64748B]">Experience:</span> <span className="font-semibold">{formData.years_experience} years</span></p>
                      <p><span className="text-[#64748B]">Insurance:</span> <span className="font-semibold text-[#10B981]">Verified</span></p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Button
                      onClick={() => setStep(3)}
                      variant="outline"
                      className="flex-1 rounded-none py-5"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="flex-1 bg-gradient-to-r from-[#D4A853] to-[#B8923A] text-[#0A1628] rounded-none py-5 uppercase tracking-wider font-bold"
                    >
                      {isSubmitting ? "Submitting..." : "Submit Application"}
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 5: Success */}
              {step === 5 && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-[#10B981]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-[#10B981]" />
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[#0A1628] mb-4">
                    Application Received!
                  </h3>
                  <p className="text-[#64748B] mb-8 max-w-md mx-auto">
                    Thank you for your interest in joining Celebrity Tree Service. 
                    We'll review your application and contact you within 3-5 business days.
                  </p>
                  <Button
                    asChild
                    className="bg-[#0A1628] text-white rounded-none px-8 py-4"
                  >
                    <a href="/">Return to Home</a>
                  </Button>
                </div>
              )}
            </div>

            {/* Quick Contact Option */}
            <div className="mt-12 text-center">
              <p className="text-[#64748B] mb-4">Prefer to talk to someone directly?</p>
              <a 
                href="tel:4803517775" 
                className="inline-flex items-center gap-2 text-[#10B981] font-bold text-xl hover:text-[#059669]"
              >
                <Phone className="w-6 h-6" />
                Call (480) 351-7775
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
